import { useCallback, useEffect, useState } from "react";
import type {
  CanvasDirection,
  CanvasItemPlacement,
  WorkflowDefinitionLike,
} from "../lib/canvas-graph";

function asDirection(value: unknown): CanvasDirection {
  return value === "LR" || value === "BT" ? value : "TB";
}

interface ApiResponse {
  items: {
    portal_id?: string;
    portalId?: string;
    flow_id?: string;
    flowId?: string;
    position_x?: number;
    positionX?: number;
    position_y?: number;
    positionY?: number;
    direction?: string;
  }[];
  definitions: {
    flowId: string;
    definition?: WorkflowDefinitionLike;
    error?: string;
  }[];
}

export interface CanvasState {
  items: CanvasItemPlacement[];
  definitions: Record<string, WorkflowDefinitionLike | undefined>;
  loading: boolean;
  error: string | null;
}

async function getJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: "include", ...init });
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => ({}));
    const b = body as { error?: string; message?: string };
    throw new Error(b.error ?? b.message ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export function useWorkflowCanvas(portalId: string | null) {
  const [state, setState] = useState<CanvasState>({
    items: [],
    definitions: {},
    loading: false,
    error: null,
  });

  const reload = useCallback(async () => {
    if (!portalId) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await getJson<ApiResponse>(
        `/api/workflow-canvas/${portalId}`,
      );
      const items: CanvasItemPlacement[] = data.items.map((r) => ({
        portalId: (r.portalId ?? r.portal_id) as string,
        flowId: (r.flowId ?? r.flow_id) as string,
        positionX: (r.positionX ?? r.position_x ?? 0) as number,
        positionY: (r.positionY ?? r.position_y ?? 0) as number,
        direction: asDirection(r.direction),
      }));
      const definitions: Record<string, WorkflowDefinitionLike | undefined> =
        {};
      for (const d of data.definitions) {
        definitions[d.flowId] = d.definition;
      }
      setState({ items, definitions, loading: false, error: null });
    } catch (err) {
      setState((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : "Unknown error",
      }));
    }
  }, [portalId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const add = useCallback(
    async (flowId: string) => {
      if (!portalId) return;
      await getJson(`/api/workflow-canvas/${portalId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ flowId }),
      });
      await reload();
    },
    [portalId, reload],
  );

  const remove = useCallback(
    async (flowId: string) => {
      if (!portalId) return;
      await getJson(`/api/workflow-canvas/${portalId}/${flowId}`, {
        method: "DELETE",
      });
      await reload();
    },
    [portalId, reload],
  );

  const updatePosition = useCallback(
    async (flowId: string, x: number, y: number) => {
      if (!portalId) return;
      setState((s) => ({
        ...s,
        items: s.items.map((it) =>
          it.flowId === flowId ? { ...it, positionX: x, positionY: y } : it,
        ),
      }));
      await getJson(`/api/workflow-canvas/${portalId}/${flowId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ x, y }),
      });
    },
    [portalId],
  );

  const updateDirection = useCallback(
    async (flowId: string, direction: CanvasDirection) => {
      if (!portalId) return;
      setState((s) => ({
        ...s,
        items: s.items.map((it) =>
          it.flowId === flowId ? { ...it, direction } : it,
        ),
      }));
      await getJson(`/api/workflow-canvas/${portalId}/${flowId}/direction`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ direction }),
      });
    },
    [portalId],
  );

  return { ...state, reload, add, remove, updatePosition, updateDirection };
}
